// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import React, { createRef } from 'react';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { ContextMenuItemSeparator } from './Items/ContextMenuItemSeparator';
import { ContextMenuItem } from './Items/ContextMenuItem';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { AccountContextMenuItem } from './Items/AccountContextMenuItem';
import { AccountUtils } from '@shared/commons/cache/AccountUtils';
import { AccountLookupContextMenuItem } from './Items/AccountLookupContextMenuItem';
import { ImageContextMenuItem } from './Items/ImageContextMenuItem';
export class NewContextMenu extends React.Component {
    constructor(props) {
        super(props);
        this.DEFAULT_MIN_WIDTH = 200;
        this.DEFAULT_MAX_WIDTH = 350;
        this.onMenuItemMouseEnter = (newHoveredMenuItem, rect) => {
            const subMenusCount = this.findCloseSubMenusCount(newHoveredMenuItem);
            if (subMenusCount !== -1) {
                const contextMenus = [...this.state.contextMenus].slice(0, subMenusCount);
                this.setState({ contextMenus }, () => {
                    this.onMenuItemMouseEnter(newHoveredMenuItem, rect);
                });
                return;
            }
            if (!isValidArray(newHoveredMenuItem.subitems)) {
                return;
            }
            this.openSubMenu(newHoveredMenuItem, rect);
        };
        this.findCloseSubMenusCount = (newHoveredMenuItem) => {
            const currentContextMenusCount = this.state.contextMenus.length;
            if (currentContextMenusCount === 1) {
                return -1;
            }
            const closeDepth = this.findMenuItemDepth(this.props.items, newHoveredMenuItem);
            if (closeDepth >= 0) {
                const newContextMenusCount = closeDepth + 1;
                if (newContextMenusCount < currentContextMenusCount) {
                    return newContextMenusCount;
                }
                else {
                    return -1;
                }
            }
            else {
                return -1;
            }
        };
        this.findMenuItemDepth = (menuItems, menuItem, currentDepth = 0) => {
            for (let i = 0; i < menuItems.length; i++) {
                const item = menuItems[i];
                if (item === menuItem) {
                    return currentDepth;
                }
                if (isValidArray(item.subitems)) {
                    const result = this.findMenuItemDepth(item.subitems, menuItem, currentDepth + 1);
                    if (result !== -1)
                        return result;
                }
            }
            return -1;
        };
        this.openSubMenu = (newHoveredMenuItem, rect) => {
            const ref = React.createRef();
            const props = {
                x: rect.right,
                y: rect.y,
                minWidth: this.props.minWidth,
                maxWidth: this.props.maxWidth,
                items: newHoveredMenuItem.subitems
            };
            const contextMenus = [...this.state.contextMenus];
            const depth = contextMenus.length;
            const subContextMenu = { ref, props, depth };
            contextMenus.push(subContextMenu);
            this.setState({ contextMenus });
        };
        this.handleOutsideClick = (event) => {
            var _a;
            const contextMenuElements = [];
            for (let i = 0; i < this.state.contextMenus.length; i++) {
                const contextMenu = this.state.contextMenus[i];
                if (!isNullOrUndefined(contextMenu.ref.current)) {
                    contextMenuElements.push(contextMenu.ref.current);
                }
            }
            for (let i = 0; i < contextMenuElements.length; i++) {
                if (contextMenuElements[i].contains(event.target)) {
                    return;
                }
            }
            const onOutsideClick = (_a = this.props.functions) === null || _a === void 0 ? void 0 : _a.onOutsideClick;
            if (!isNullOrUndefined(onOutsideClick)) {
                onOutsideClick(event);
            }
            else {
                this.closeContextMenu();
            }
        };
        this.handleMouseMove = (event) => {
            var _a;
            if (!isNullOrUndefined((_a = this.props.functions) === null || _a === void 0 ? void 0 : _a.onMouseLeave) && this.processMouseLeave(event)) {
                this.props.functions.onMouseLeave(event);
            }
        };
        this.initializeContextMenu = () => {
            const contextMenu = { depth: 0, ref: createRef(), props: this.props };
            const contextMenus = [contextMenu];
            const contextMenusLocation = [{ left: this.props.x, top: this.props.y }];
            this.setState({
                contextMenus,
                contextMenusLocation
            }, () => {
                var _a, _b;
                const contextMenuRef = this.state.contextMenus[0].ref;
                HtmlScroll.addScroll(contextMenuRef.current);
                (_b = (_a = this.props.functions) === null || _a === void 0 ? void 0 : _a.onContextMenuInitialized) === null || _b === void 0 ? void 0 : _b.call(_a, contextMenuRef);
                this.correctMainContextMenuLocation();
            });
        };
        this.closeContextMenu = () => {
            hideContextMenu();
        };
        this.correctMainContextMenuLocation = () => {
            const mainContextMenuLocation = this.getContextMenuLocation(this.state.contextMenus[0]);
            const contextMenusLocation = [...this.state.contextMenusLocation];
            contextMenusLocation[0] = mainContextMenuLocation;
            this.setState({ contextMenusLocation });
        };
        this.correctSubContextMenuLocation = () => {
            const contextMenusLocation = [...this.state.contextMenusLocation].slice(0, 1);
            if (this.state.contextMenus.length > 1) {
                for (let i = 1; i < this.state.contextMenus.length; i++) {
                    const subContextMenu = this.state.contextMenus[i];
                    const subContextMenuLocation = this.getContextMenuLocation(subContextMenu);
                    contextMenusLocation.push(subContextMenuLocation);
                }
            }
            this.setState({ contextMenusLocation });
        };
        this.getContextMenuLocation = (contextMenu) => {
            let left = contextMenu.props.x;
            let top = contextMenu.props.y;
            const element = contextMenu.ref.current;
            if (isNullOrUndefined(element)) {
                return { left, top };
            }
            left = this.props.isXRight ? contextMenu.props.x - element.offsetWidth : element.offsetLeft;
            top = this.props.isYBottom ? contextMenu.props.y - element.offsetHeight : element.offsetTop;
            //TODO needed RTL logic
            // Correct vertical position
            if (top + element.offsetHeight > window.innerHeight) {
                top = window.innerHeight - element.offsetHeight;
            }
            else if (top < 0) {
                top = 0;
            }
            // Correct horizontal position
            const isMain = contextMenu.depth === 0;
            if (left + element.offsetWidth > window.innerWidth) {
                if (!isMain) {
                    const parentLocation = this.state.contextMenusLocation[contextMenu.depth - 1];
                    left = parentLocation.left - element.offsetWidth;
                }
                else {
                    // For main context menu, align to right edge of screen
                    left = window.innerWidth - element.offsetWidth;
                }
            }
            else if (left < 0) {
                left = 0;
            }
            return { left, top };
        };
        this.getContextMenuItemsStyle = (items) => {
            const result = {
                isAllowIcon: false,
                isAllowSubItems: false,
                accountModeWidth: 0,
                isVisibleInstrumentType: false
            };
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.visible === false) {
                    continue;
                }
                if (isValidString(item.svgImage) || !isNullOrUndefined(item.icon) || item.canCheck) {
                    result.isAllowIcon = true;
                }
                if (isValidArray(item.subitems)) {
                    result.isAllowSubItems = true;
                }
                if (item.type === NewContextMenuItemType.Account) {
                    const account = item.value;
                    const accountModeWidth = AccountUtils.GetAccountModeWidth(account);
                    result.accountModeWidth = Math.max(result.accountModeWidth, accountModeWidth);
                    if (!result.isVisibleInstrumentType) {
                        result.isVisibleInstrumentType = AccountUtils.needAddType(account);
                    }
                }
            }
            return result;
        };
        this.filterVisibleItems = (items) => {
            if (!isValidArray(items)) {
                return [];
            }
            const visibleItems = items.filter((item) => item.visible !== false);
            const result = [];
            let lastWasSeparator = false;
            for (const item of visibleItems) {
                const isSeparator = item.type === NewContextMenuItemType.Separator;
                if (isSeparator) {
                    if (lastWasSeparator) {
                        continue;
                    }
                    result.push(item);
                    lastWasSeparator = true;
                }
                else {
                    result.push(item);
                    lastWasSeparator = false;
                }
            }
            if (result.length > 0 && result[result.length - 1].type === NewContextMenuItemType.Separator) {
                result.pop();
            }
            if (result.length > 0 && result[0].type === NewContextMenuItemType.Separator) {
                result.shift();
            }
            return result;
        };
        this.processMouseLeave = (event) => {
            const contextMenus = this.state.contextMenus;
            for (let i = 0; i < contextMenus.length; i++) {
                const contextMenu = contextMenus[i];
                const element = contextMenu.ref.current;
                if (isNullOrUndefined(element)) {
                    continue;
                }
                const elementRect = element.getBoundingClientRect();
                if (elementRect.contains(event.x, event.y)) {
                    return false;
                }
            }
            return true;
        };
        this.state = {
            contextMenus: [],
            contextMenusLocation: []
        };
    }
    componentDidMount() {
        this.initializeContextMenu();
        document.addEventListener('mousedown', this.handleOutsideClick);
        document.addEventListener('mousemove', this.handleMouseMove);
    }
    componentWillUnmount() {
        var _a, _b;
        if (!isNullOrUndefined(this.props.functions)) {
            (_b = (_a = this.props.functions).closeCallback) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        document.removeEventListener('mousedown', this.handleOutsideClick);
        document.removeEventListener('mousemove', this.handleMouseMove);
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        const isFullUpdate = this.props.items !== prevProps.items;
        if (isFullUpdate) {
            this.initializeContextMenu();
            return;
        }
        const isOpenedContextMenu = prevState.contextMenus.length < this.state.contextMenus.length;
        if (isOpenedContextMenu) {
            if (this.state.contextMenus.length === 1) {
                this.correctMainContextMenuLocation();
            }
            else {
                this.correctSubContextMenuLocation();
            }
            return;
        }
        const isClosedContextMenu = prevState.contextMenus.length > this.state.contextMenus.length;
        if (isClosedContextMenu) {
            this.correctSubContextMenuLocation();
        }
    }
    render() {
        if (!isValidArray(this.props.items)) {
            return null;
        }
        const contextMenus = [...this.state.contextMenus];
        if (!isValidArray(contextMenus)) {
            return null;
        }
        // First sub menu should be before main menu, to be under it in z-index
        if (contextMenus.length > 1) {
            const item = contextMenus.splice(1, 1)[0];
            contextMenus.splice(0, 0, item);
        }
        return React.createElement(React.Fragment, null, contextMenus.map((contextMenu) => this.renderContextMenu(contextMenu)));
    }
    renderContextMenu(contextMenu) {
        var _a, _b, _c;
        const contextMenuProps = contextMenu.props;
        const visibleItems = this.filterVisibleItems(contextMenuProps.items);
        if (!isValidArray(visibleItems)) {
            return null;
        }
        const maxWidth = (_a = contextMenuProps.maxWidth) !== null && _a !== void 0 ? _a : this.DEFAULT_MAX_WIDTH;
        const depth = contextMenu.depth;
        const isMain = depth === 0;
        let location = this.state.contextMenusLocation[depth];
        if (isNullOrUndefined(location)) {
            location = { left: contextMenu.props.x, top: contextMenu.props.y };
        }
        const { isAllowIcon, isAllowSubItems, accountModeWidth, isVisibleInstrumentType } = this.getContextMenuItemsStyle(visibleItems);
        const style = {
            left: location.left,
            top: location.top,
            maxWidth
        };
        if (isMain) {
            style.minWidth = (_b = contextMenuProps.minWidth) !== null && _b !== void 0 ? _b : this.DEFAULT_MIN_WIDTH;
            style.maxHeight = (_c = contextMenuProps.maxHeight) !== null && _c !== void 0 ? _c : windowHeight();
        }
        return (React.createElement("div", { className: "new-context-menu", key: depth, ref: contextMenu.ref, style: style },
            React.createElement("ul", null, visibleItems.map((item, index) => (React.createElement("li", { id: item.id, key: index }, this.renderContextMenuItem(item, isAllowIcon, isAllowSubItems, accountModeWidth, isVisibleInstrumentType)))))));
    }
    renderContextMenuItem(item, isAllowIcon, isAllowSubItems, accountModeWidth, isVisibleInstrumentType) {
        switch (item.type) {
            case NewContextMenuItemType.Separator:
                return React.createElement(ContextMenuItemSeparator, null);
            case NewContextMenuItemType.Default:
            case NewContextMenuItemType.ExternalTool:
                return (React.createElement(ContextMenuItem, { isAllowIcon: isAllowIcon, isAllowSubItems: isAllowSubItems, item: item, onCloseContextMenu: this.closeContextMenu, onMouseEnter: this.onMenuItemMouseEnter }));
            case NewContextMenuItemType.Account:
                return (React.createElement(AccountContextMenuItem, { isVisibleInstrumentType: isVisibleInstrumentType, accountModeWidth: accountModeWidth, item: item, onCloseContextMenu: this.closeContextMenu }));
            case NewContextMenuItemType.AccountLookup:
                return React.createElement(AccountLookupContextMenuItem, { item: item, onCloseContextMenu: this.closeContextMenu });
            case NewContextMenuItemType.Image:
                return React.createElement(ImageContextMenuItem, { item: item, onCloseContextMenu: this.closeContextMenu });
        }
    }
}
